/**
 * Module: Margin Master
 * Developed By: Farhan Khan
 * Date: 23-02-2026
 * Description: This module manages cost-based margin slabs.
 * Reason: Margin logic separated from pricing_master to keep calculation modular,
 * scalable and business-configurable without affecting item-level pricing.
 *
 * SUPER_ADMIN only - Table view with pagination, Add/Edit modals (same pattern as Pricing Master).
 */

import React, { useEffect, useState } from "react";
import {
    Button,
    Col,
    Container,
    Form,
    Modal,
    Row,
    Spinner,
    Stack,
    Table,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import fuziElevatorsApi from "../../api/fuziElevatorsApi";
import { ROUTES } from "../../constants/webRouting";
import { MARGIN_TYPES, LIFT_TYPES } from "../../constants/offerConstants";
import { getUserDetails, formatDateForView } from "../../utils/helper";
import ".././../assets/css/ListComponentsTable.css";
import PubSub from "pubsub-js";

const INITIAL_FORM = {
    min_cost: "",
    max_cost: "",
    margin_percentage: "",
    margin_type: "",
    margin_value: "",
    lift_type: "",
    is_active: true,
};

const INITIAL_ERRORS = {};

const MarginMasterList = () => {
    const userInfo = getUserDetails();
    const navigate = useNavigate();

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(20);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [includeInactive, setIncludeInactive] = useState(false);

    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [formData, setFormData] = useState(INITIAL_FORM);
    const [errors, setErrors] = useState(INITIAL_ERRORS);
    const [submitting, setSubmitting] = useState(false);
    const [editId, setEditId] = useState(null);
    const [deleteRecord, setDeleteRecord] = useState(null);

    useEffect(() => {
        if (userInfo?.userrole !== "SUPER_ADMIN") {
            navigate(ROUTES.HOME);
            return;
        }
        fetchData();
    }, [page, limit, includeInactive]);

    const fetchData = async () => {
        setLoading(true);
        const result = await fuziElevatorsApi.fetchMarginMaster({
            page,
            limit,
            include_inactive: includeInactive ? "true" : "false",
        });
        setLoading(false);
        if (result?.errors) {
            PubSub.publish("RECORD_ERROR_TOAST", {
                title: "Error",
                message: Array.isArray(result.errors) ? result.errors.map((e) => e.msg || e.message).join(", ") : result.errors,
            });
            setData([]);
            return;
        }
        if (result?.data) {
            setData(result.data);
            setTotal(result.total ?? 0);
            setTotalPages(result.totalPages ?? 1);
        } else {
            setData([]);
        }
    };

    const handleAddClick = () => {
        setFormData(INITIAL_FORM);
        setErrors(INITIAL_ERRORS);
        setShowAddModal(true);
    };

    const handleEditClick = (row) => {
        setEditId(row.id);
        setFormData({
            min_cost: row.min_cost != null ? String(row.min_cost) : "",
            max_cost: row.max_cost != null ? String(row.max_cost) : "",
            margin_percentage: row.margin_percentage != null ? String(row.margin_percentage) : "",
            margin_type: row.margin_type || "",
            margin_value: row.margin_value != null ? String(row.margin_value) : "",
            lift_type: row.lift_type || "",
            is_active: row.is_active !== false,
        });
        setErrors(INITIAL_ERRORS);
        setShowEditModal(true);
    };

    const handleDeleteClick = (row) => {
        setDeleteRecord(row);
        setShowDeleteModal(true);
    };

    const validateForm = () => {
        const newErrors = {};
        const minCost = parseFloat(formData.min_cost);
        const maxCost = parseFloat(formData.max_cost);
        const marginPct = parseFloat(formData.margin_percentage);

        if (formData.min_cost === "" || formData.min_cost === null || formData.min_cost === undefined) {
            newErrors.min_cost = "Min cost is required";
        } else if (isNaN(minCost) || minCost < 0) {
            newErrors.min_cost = "Min cost must be >= 0";
        }

        if (formData.max_cost === "" || formData.max_cost === null || formData.max_cost === undefined) {
            newErrors.max_cost = "Max cost is required";
        } else if (isNaN(maxCost) || maxCost <= (isNaN(minCost) ? -1 : minCost)) {
            newErrors.max_cost = "Max cost must be greater than min cost";
        }

        if (formData.margin_percentage === "" || formData.margin_percentage === null || formData.margin_percentage === undefined) {
            newErrors.margin_percentage = "Margin % is required";
        } else if (isNaN(marginPct) || marginPct < 0 || marginPct > 100) {
            newErrors.margin_percentage = "Margin % must be between 0 and 100";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const getPayload = () => {
        const payload = {
            min_cost: parseFloat(formData.min_cost) || 0,
            max_cost: parseFloat(formData.max_cost) || 0,
            margin_percentage: parseFloat(formData.margin_percentage) || 0,
            is_active: formData.is_active,
        };
        if (formData.margin_type) payload.margin_type = formData.margin_type;
        if (formData.margin_value !== "") payload.margin_value = parseFloat(formData.margin_value) || 0;
        if (formData.lift_type) payload.lift_type = formData.lift_type;
        return payload;
    };

    const handleAddSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;
        setSubmitting(true);
        const payload = getPayload();
        const result = await fuziElevatorsApi.createMarginMaster(payload);
        setSubmitting(false);
        if (result?.errors) {
            const errMsg = Array.isArray(result.errors)
                ? result.errors.map((e) => e.msg || e.message).join(", ")
                : result.errors;
            setErrors({ submit: errMsg });
            PubSub.publish("RECORD_ERROR_TOAST", { title: "Error", message: errMsg });
            return;
        }
        if (result?.success) {
            setShowAddModal(false);
            fetchData();
            PubSub.publish("RECORD_SAVED_TOAST", { title: "Success", message: "Margin slab created successfully" });
        }
    };

    const handleEditSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;
        setSubmitting(true);
        const payload = getPayload();
        const result = await fuziElevatorsApi.updateMarginMaster(editId, payload);
        setSubmitting(false);
        if (result?.errors) {
            const errMsg = Array.isArray(result.errors)
                ? result.errors.map((e) => e.msg || e.message).join(", ")
                : result.errors;
            setErrors({ submit: errMsg });
            PubSub.publish("RECORD_ERROR_TOAST", { title: "Error", message: errMsg });
            return;
        }
        if (result?.success) {
            setShowEditModal(false);
            setEditId(null);
            fetchData();
            PubSub.publish("RECORD_SAVED_TOAST", { title: "Success", message: "Margin slab updated successfully" });
        }
    };

    const handleDeleteConfirm = async () => {
        if (!deleteRecord) return;
        setSubmitting(true);
        const result = await fuziElevatorsApi.deleteMarginMaster(deleteRecord.id);
        setSubmitting(false);
        setShowDeleteModal(false);
        setDeleteRecord(null);
        if (result?.errors) {
            PubSub.publish("RECORD_ERROR_TOAST", {
                title: "Error",
                message: Array.isArray(result.errors) ? result.errors.map((e) => e.msg || e.message).join(", ") : result.errors,
            });
            return;
        }
        if (result?.success) {
            fetchData();
            PubSub.publish("RECORD_SAVED_TOAST", { title: "Success", message: "Margin slab deactivated successfully" });
        }
    };

    const handleFormChange = (name, value) => {
        setFormData((prev) => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }));
        if (errors.submit) setErrors((prev) => ({ ...prev, submit: null }));
    };

    const handleNumericChange = (name, e) => {
        const v = e.target.value;
        if (v === "" || /^\d*\.?\d*$/.test(v)) {
            setFormData((prev) => ({ ...prev, [name]: v }));
            if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }));
            if (errors.submit) setErrors((prev) => ({ ...prev, submit: null }));
        }
    };

    if (userInfo?.userrole !== "SUPER_ADMIN") {
        return null;
    }

    const formFieldsContent = (
        <>
            <Form.Group className="mb-3">
                <Form.Label>Min Cost <span className="text-danger">*</span></Form.Label>
                <Form.Control
                    type="text"
                    inputMode="decimal"
                    name="min_cost"
                    placeholder="0.00"
                    value={formData.min_cost}
                    onChange={(e) => handleNumericChange("min_cost", e)}
                    isInvalid={!!errors.min_cost}
                />
                <Form.Control.Feedback type="invalid">{errors.min_cost}</Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
                <Form.Label>Max Cost <span className="text-danger">*</span></Form.Label>
                <Form.Control
                    type="text"
                    inputMode="decimal"
                    name="max_cost"
                    placeholder="0.00"
                    value={formData.max_cost}
                    onChange={(e) => handleNumericChange("max_cost", e)}
                    isInvalid={!!errors.max_cost}
                />
                <Form.Control.Feedback type="invalid">{errors.max_cost}</Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
                <Form.Label>Margin % <span className="text-danger">*</span></Form.Label>
                <Form.Control
                    type="text"
                    inputMode="decimal"
                    name="margin_percentage"
                    placeholder="0.00"
                    value={formData.margin_percentage}
                    onChange={(e) => handleNumericChange("margin_percentage", e)}
                    isInvalid={!!errors.margin_percentage}
                />
                <Form.Control.Feedback type="invalid">{errors.margin_percentage}</Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
                <Form.Label>Margin Type</Form.Label>
                <Form.Select
                    name="margin_type"
                    value={formData.margin_type}
                    onChange={(e) => handleFormChange("margin_type", e.target.value)}
                >
                    <option value="">Select margin type</option>
                    {MARGIN_TYPES.map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                    ))}
                </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
                <Form.Label>Margin Value</Form.Label>
                <Form.Control
                    type="text"
                    inputMode="decimal"
                    name="margin_value"
                    placeholder="0.00"
                    value={formData.margin_value}
                    onChange={(e) => handleNumericChange("margin_value", e)}
                />
            </Form.Group>

            <Form.Group className="mb-3">
                <Form.Label>Lift Type</Form.Label>
                <Form.Select
                    name="lift_type"
                    value={formData.lift_type}
                    onChange={(e) => handleFormChange("lift_type", e.target.value)}
                >
                    <option value="">Select lift type</option>
                    {LIFT_TYPES.map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                    ))}
                </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
                <Form.Check
                    type="switch"
                    id="is_active"
                    label="Active"
                    checked={formData.is_active}
                    onChange={(e) => handleFormChange("is_active", e.target.checked)}
                />
            </Form.Group>

            {errors.submit && (
                <div className="alert alert-danger py-2">{errors.submit}</div>
            )}
        </>
    );

    return (
        <Container fluid>
            <Row className="mb-3">
                <Col>
                    <h5 className="mb-0">Margin Master</h5>
                </Col>
                <Col xs="auto">
                    <Stack direction="horizontal" gap={2}>
                        <Form.Check
                            type="switch"
                            id="includeInactive"
                            label="Include inactive"
                            checked={includeInactive}
                            onChange={(e) => {
                                setIncludeInactive(e.target.checked);
                                setPage(1);
                            }}
                        />
                        <Button size="sm" className="btn-create" onClick={handleAddClick}>
                            Add Slab
                        </Button>
                    </Stack>
                </Col>
            </Row>

            {loading ? (
                <div className="text-center py-5">
                    <Spinner animation="border" /> <span className="ms-2">Loading...</span>
                </div>
            ) : (
                <>
                    <div className="table-scroll my-3">
                        <Table striped className="shadow rounded sm" responsive>
                            <thead className="table-light">
                                <tr>
                                    <th className="bg-custom text-white">Min Cost</th>
                                    <th className="bg-custom text-white">Max Cost</th>
                                    <th className="bg-custom text-white">Margin %</th>
                                    <th className="bg-custom text-white">Active</th>
                                    <th className="bg-custom text-white">Created</th>
                                    <th className="bg-custom text-white text-center">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.length === 0 ? (
                                    <tr>
                                        <td colSpan={6} className="text-center text-muted py-4">
                                            No margin slabs found.
                                        </td>
                                    </tr>
                                ) : (
                                    data.map((row) => (
                                        <tr key={row.id}>
                                            <td>{row.min_cost != null ? Number(row.min_cost).toLocaleString() : "-"}</td>
                                            <td>{row.max_cost != null ? Number(row.max_cost).toLocaleString() : "-"}</td>
                                            <td>{row.margin_percentage != null ? Number(row.margin_percentage).toFixed(2) : "-"}</td>
                                            <td>
                                                <span className={`badge ${row.is_active ? "bg-success" : "bg-secondary"}`}>
                                                    {row.is_active ? "Yes" : "No"}
                                                </span>
                                            </td>
                                            <td>{row.createddate ? formatDateForView(row.createddate) : "-"}</td>
                                            <td className="text-center">
                                                <Button
                                                    size="sm"
                                                    variant="outline-primary"
                                                    className="me-1"
                                                    onClick={() => handleEditClick(row)}
                                                >
                                                    <i className="fa-solid fa-pen"></i>
                                                </Button>
                                                {row.is_active && (
                                                    <Button
                                                        size="sm"
                                                        variant="outline-danger"
                                                        onClick={() => handleDeleteClick(row)}
                                                    >
                                                        <i className="fa-solid fa-trash"></i>
                                                    </Button>
                                                )}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </Table>
                    </div>

                    {totalPages > 1 && (
                        <div className="d-flex justify-content-between align-items-center mt-2">
                            <span className="text-muted">
                                Showing {(page - 1) * limit + 1}-{Math.min(page * limit, total)} of {total}
                            </span>
                            <div className="d-flex gap-1">
                                <Button
                                    size="sm"
                                    variant="outline-secondary"
                                    disabled={page <= 1}
                                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                                >
                                    Previous
                                </Button>
                                <span className="align-self-center px-2">
                                    Page {page} of {totalPages}
                                </span>
                                <Button
                                    size="sm"
                                    variant="outline-secondary"
                                    disabled={page >= totalPages}
                                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                                >
                                    Next
                                </Button>
                            </div>
                        </div>
                    )}
                </>
            )}

            {/* Add Modal */}
            <Modal show={showAddModal} onHide={() => setShowAddModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Add Margin Slab</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleAddSubmit}>
                    <Modal.Body>
                        {formFieldsContent}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowAddModal(false)} disabled={submitting}>
                            Cancel
                        </Button>
                        <Button variant="primary" type="submit" disabled={submitting}>
                            {submitting ? <><Spinner animation="border" size="sm" className="me-1" />Creating...</> : "Create"}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Edit Modal */}
            <Modal show={showEditModal} onHide={() => { setShowEditModal(false); setEditId(null); }} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Edit Margin Slab</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleEditSubmit}>
                    <Modal.Body>
                        {formFieldsContent}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => { setShowEditModal(false); setEditId(null); }} disabled={submitting}>
                            Cancel
                        </Button>
                        <Button variant="primary" type="submit" disabled={submitting}>
                            {submitting ? <><Spinner animation="border" size="sm" className="me-1" />Updating...</> : "Update"}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Delete Confirm Modal */}
            <Modal show={showDeleteModal} onHide={() => { setShowDeleteModal(false); setDeleteRecord(null); }} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Deactivate Margin Slab</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Are you sure you want to deactivate this margin slab? It will be soft-deleted (is_active = false).
                    {deleteRecord && (
                        <div className="mt-2 p-2 bg-light rounded">
                            <strong>{deleteRecord.min_cost?.toLocaleString()} - {deleteRecord.max_cost?.toLocaleString()}</strong> ({deleteRecord.margin_percentage}% margin)
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => { setShowDeleteModal(false); setDeleteRecord(null); }} disabled={submitting}>
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={handleDeleteConfirm} disabled={submitting}>
                        {submitting ? <><Spinner animation="border" size="sm" className="me-1" />Deactivating...</> : "Deactivate"}
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default MarginMasterList;
